'use client';

import { logger } from '@magicschool/logger';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useCallback, useEffect, useState } from 'react';
import type { EdlinkWidget } from '@/declarations/edlinkWidget';
import { EDLINK_DOMAIN, edlinkRedirectUri } from '@/features/edlink/constants';

export const EdlinkWidgetClient = () => {
  const pathname = usePathname();
  const [widget, setWidget] = useState<EdlinkWidget | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const shouldHaveWidget = pathname.includes('/auth/') || pathname.includes('/s/login') || pathname.includes('/s/join');

  const onLoad = useCallback(() => {
    setIsScriptLoaded(true);
  }, []);

  // Initialize widget when script loads and shouldHaveWidget is true
  useEffect(() => {
    if (!isScriptLoaded || !shouldHaveWidget) return;

    let edlinkWidget: EdlinkWidget;
    try {
      const edlinkInstance = new window.Edlink({
        client_id: process.env.NEXT_PUBLIC_EDLINK_CLIENT_ID,
        redirect_uri: edlinkRedirectUri(),
        colors: {
          primary: '#0088ff',
          background: '#181818',
          background_secondary: '#212121',
          background_tertiary: '#424242',
          text: '#ffffff',
          subtext: '#8395a7',
          button_text: '#ffffff',
        },
        // Edlink's widget bundle reads `theme.primary_color` unconditionally and throws when
        // it's missing, aborting mount before the widget ever renders. GLINT-333.
        theme: {
          primary_color: '#0088ff',
          text_color: '#ffffff',
        },
      });

      edlinkWidget = edlinkInstance.createWidget();
      edlinkWidget.activatePlugin({ name: 'authentication' });

      const iframe = document.getElementById('edlink-widget');
      if (iframe) {
        // just doing this in english for now as this is outside the locale provider
        // and just to pass our accessibility audit
        iframe.setAttribute('aria-label', '1-click SSO for Enterprise Users');
      }

      setWidget(edlinkWidget);
    } catch (error) {
      logger.error('Failed to initialize Edlink widget:', error);
    }

    return () => {
      edlinkWidget?.destroy();
    };
  }, [isScriptLoaded, shouldHaveWidget]);

  useEffect(() => {
    if (!widget) return;
    if (shouldHaveWidget) {
      widget.show();
    } else {
      widget.destroy();
      setWidget(null);
    }
  }, [shouldHaveWidget, widget]);
  if (!shouldHaveWidget) return null;

  return <Script src={`${EDLINK_DOMAIN}/widgets/edlink@1.0.0.js`} onLoad={onLoad} />;
};
